import React from "react";
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Text,
} from "@chakra-ui/react";

/**
 * Confirm dialog for the home_collage_order reassign flow (Redmine #266343):
 * shown when the backend rejects a save because another Masterlist record
 * already holds the requested collage position. Confirming resends the same
 * save with confirm_home_collage_order_reassign: true, which atomically
 * clears the other record's position and assigns it to this one.
 *
 * Follows this codebase's existing hand-built AlertDialog + useDisclosure +
 * cancelRef convention (see e.g. FileList.js's delete-confirmation dialog)
 * rather than introducing a new pattern -- there is no shared generic
 * ConfirmDialog component in this codebase today.
 */
export default function HomeCollageReassignDialog({
  isOpen,
  conflict,
  isConfirming,
  cancelRef,
  onConfirm,
  onCancel,
}) {
  if (!conflict) return null;

  const holderName = conflict.display_name || conflict.alias;

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      closeOnOverlayClick={!isConfirming}
      closeOnEsc={!isConfirming}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Position already assigned
          </AlertDialogHeader>

          <AlertDialogBody>
            <Text>
              Home collage order <strong>{conflict.value}</strong> is already
              assigned to <strong>{holderName}</strong>.
            </Text>
            <Text mt={2}>
              Taking this position will remove it from {holderName}&apos;s
              record. Do you want to continue?
            </Text>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={onCancel}
              isDisabled={isConfirming}
            >
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={onConfirm}
              ml={3}
              isLoading={isConfirming}
            >
              Take this position
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
